import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, Mail, Loader2, KeyRound } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { ForgotForm, ResetForm, ChangePasswordForm } from "./InboxAuth";
import { DraftBox } from "./DraftBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "ce-inbox-token";
const field = "w-full border-b border-white/20 bg-transparent py-3 text-lg text-white placeholder:text-zinc-500 outline-none focus:border-[var(--acid)]";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong";
};

const Login = ({ onLogin, onForgot }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      onLogin(data.token);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="inbox-login-form" onSubmit={submit} className="mx-auto mt-24 max-w-md space-y-6">
      <h1 className="font-name text-6xl">Inbox<span className="italic text-[var(--acid)]">.</span></h1>
      <p className="text-zinc-400">Solo para Camila — Only for Camila</p>
      <input data-testid="inbox-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
      <input data-testid="inbox-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={field} />
      <button data-testid="inbox-login-btn" disabled={busy} className="flex items-center gap-2 rounded-full bg-[var(--acid)] px-8 py-3 font-semibold text-[var(--ink)] disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} Entrar
      </button>
      <button type="button" data-testid="inbox-forgot-link" onClick={onForgot} className="block text-sm text-zinc-400 underline hover:text-white">¿Olvidaste tu contraseña?</button>
    </form>
  );
};

const MessageCard = ({ m, onDelete, token }) => (
  <article data-testid={`inbox-message-${m.id}`} className="rounded-3xl border border-white/10 bg-[var(--surface)] p-6 sm:p-8">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 className="font-display text-2xl font-medium">{m.name}</h3>
        <a href={`mailto:${m.email}`} className="mt-1 flex items-center gap-2 text-[var(--acid)] hover:underline"><Mail size={14} />{m.email}</a>
      </div>
      <div className="flex items-center gap-3">
        {m.project_type && <span className="rounded-full bg-[var(--acid)] px-3 py-1 font-mono text-[11px] uppercase text-[var(--ink)]">{m.project_type}</span>}
        <span className="font-mono text-xs text-zinc-400">{new Date(m.created_at).toLocaleString()}</span>
        <button data-testid={`inbox-delete-${m.id}`} onClick={() => onDelete(m.id)} className="grid h-9 w-9 place-items-center rounded-full border border-white/15 hover:bg-[var(--signal)]" aria-label="delete"><Trash2 size={15} /></button>
      </div>
    </div>
    <p className="mt-5 whitespace-pre-wrap leading-relaxed text-zinc-200">{m.message}</p>
    <DraftBox m={m} token={token} />
  </article>
);

export default function Inbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const [msgs, setMsgs] = useState(null);
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("reset") ? "reset" : "login"));
  const [showChange, setShowChange] = useState(false);
  const resetToken = new URLSearchParams(window.location.search).get("reset");
  const resetDone = () => { window.history.replaceState(null, "", "/inbox"); setMode("login"); };

  const logout = useCallback(() => { sessionStorage.removeItem(KEY); setToken(null); setMsgs(null); }, []);
  const login = (t) => { sessionStorage.setItem(KEY, t); setToken(t); };
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMsgs(Array.isArray(r.data) ? r.data : []))
      .catch(() => logout());
  }, [token, logout]);

  const remove = async (id) => {
    if (!window.confirm("¿Borrar este mensaje?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, auth);
      setMsgs((l) => l.filter((m) => m.id !== id));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div data-testid="inbox-page" className="min-h-screen px-4 pb-24 pt-8 sm:px-8">
      <header className="mx-auto flex max-w-4xl items-center justify-between">
        <a href="/" data-testid="inbox-home-link" className="flex items-center gap-3"><LogoMark /><span className="font-display text-sm">eterovic<span className="text-[var(--acid)]">®</span></span></a>
        {token && (
          <div className="flex items-center gap-5">
            <button data-testid="inbox-change-password-btn" onClick={() => setShowChange((s) => !s)} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"><KeyRound size={15} />Contraseña</button>
            <button data-testid="inbox-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"><LogOut size={15} />Salir</button>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-4xl">
        {!token ? (
          mode === "reset" && resetToken ? <ResetForm token={resetToken} onDone={resetDone} />
            : mode === "forgot" ? <ForgotForm onBack={() => setMode("login")} />
            : <Login onLogin={login} onForgot={() => setMode("forgot")} />
        ) : (
          <>
            {showChange && <ChangePasswordForm token={token} onClose={() => setShowChange(false)} />}
            <h1 className="mt-16 font-name text-6xl">Mensajes <span className="italic text-[var(--acid)]">{msgs ? msgs.length : ""}</span></h1>
            <div className="mt-10 space-y-4">
              {msgs === null && <Loader2 className="animate-spin" />}
              {msgs?.length === 0 && <p data-testid="inbox-empty" className="text-zinc-400">Aún no hay mensajes. — No messages yet.</p>}
              {msgs?.map((m) => <MessageCard key={m.id} m={m} onDelete={remove} token={token} />)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
